// 조합비 결재자 관리

import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Button from "@/js/common/ui/Button";
import Util from "@/js/common/Util";
import SelectBox from "@/js/common/ui/input/SelectBox";
import Modal from "@/js/common/ui/modal/Modal";
import DialogTitleBar from "@/js/system/common/DialogTitleBar";
import MbrPickDialog from "./dialog/MbrPickDialog";
import { useRecoilValue } from "recoil";
import * as Atom from "@/js/common/Atom";

const SysAprvrMng2 = () => {
  const userInfo = useRecoilValue(Atom.userInfo);

  const [upOgnzCd, setUpOgnzCd] = React.useState<string>(userInfo.upOgnzCd);
  const [ognzCd, setOgnzCd] = React.useState<string>(userInfo.ognzCd);
  const [callTime, setCallTime] = React.useState<Date>(new Date());

  const [dataArr, setDataArr] = React.useState<any[]>([]);
  const [memberPickOpen, setMemberPickOpen] = React.useState<boolean>(false);

  // 모달 모드: add / edit
  const [pickMode, setPickMode] = React.useState<"add" | "edit">("edit");
  const [pickTargetAtrzStngId, setPickTargetAtrzStngId] = React.useState<string>("");

  // 목록 조회 
  React.useEffect(() => {
    if (!ognzCd) {
      setDataArr([]);
      return;
    }
    AppContext.showSpinner();
    Axios.dataAccess({
      url: "system/sys/aprvr/select-aprvr-list.do",
      methodType: "post",
      paramType: "json",
      paramData: { orgCd: ognzCd, atrzSeCd: "01" },
      onSuccessFn: (res: any) => {
        setDataArr(res?.result?.list || []);
        AppContext.hideSpinner();
      },
    });
  }, [callTime, ognzCd]);

  // 저장 
  const saveAll = (rows: any[], successMsg: string) => {
    const list = rows || [];

    // 서버가 atrzStpSeq를 필요로 할 수 있으니 idx+1만 넣어줌
    const dataList = list.map((r: any, idx: number) => ({
      orgCd: ognzCd,
      atrzSeCd: "01",
      atrzStpSeq: idx + 1,
      picUserId: String(r.picUserId || "").trim(),
      useYn: r.useYn || "Y",
    }));

    // 필수: 담당자ID
    const missing = dataList.find((x: any) => !x.picUserId);
    if (missing) {
      AppContext.openAlert({
        title: "알림",
        msg: "담당자ID는 필수입니다.",
        icon: "info",
        closeText: "확인",
      });
      return;
    }

    // 중복: 담당자ID
    const map = new Map<string, number>();
    for (const x of dataList) {
      const id = String(x.picUserId || "").trim();
      map.set(id, (map.get(id) || 0) + 1);
    }
    const hasDup = Array.from(map.values()).some((v) => v >= 2);
    if (hasDup) {
      AppContext.openAlert({
        title: "알림",
        msg: "결재자에 중복된 담당자ID가 있습니다. 중복을 제거해주세요.",
        icon: "warning",
        closeText: "확인",
      });
      return;
    }

    AppContext.showSpinner();
    Axios.dataAccess({
      url: "system/sys/aprvr/save-aprvr.do",
      methodType: "post",
      paramType: "json",
      paramData: { orgCd: ognzCd, atrzSeCd: "01", dataList },
      onSuccessFn: () => {
        AppContext.hideSpinner();
        AppContext.openAlert({
          title: "알림",
          msg: successMsg,
          icon: "info",
          closeText: "확인",
        });

        // 화면 즉시 반영 + 재조회
        setDataArr(list);
        setCallTime(new Date());
      },
    });
  };

  // 추가: 행 먼저 만들지 않고 모달 오픈
  const onAdd = () => {
    setPickMode("add");
    setPickTargetAtrzStngId("");
    setMemberPickOpen(true);
  };

  // 수정: 해당 row를 지정하고 모달 오픈 → 선택하면 교체 후 저장
  const openMemberPickForEdit = (atrzStngId: any) => {
    setPickMode("edit");
    setPickTargetAtrzStngId(String(atrzStngId));
    setMemberPickOpen(true);
  };

  const closeMemberPick = () => {
    setMemberPickOpen(false);
    setPickTargetAtrzStngId("");
  };

  // 모달에서 담당자 선택 시 add/edit 처리 후 즉시 저장
  const onPickMember = (item: any) => {
    const pickedUserId = String(item?.userId || "").trim();
    const pickedUserNm = String(item?.userNm || "").trim();
    const pickedJbpsSeNm = String(item?.jbpsSeNm || "").trim();

    if (!pickedUserId) {
      closeMemberPick();
      return;
    }

    // add: 그냥 뒤에 붙임
    if (pickMode === "add") {
      const nextRows = (dataArr || []).concat({
        atrzStngId: `NEW_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        orgCd: ognzCd,
        atrzSeCd: "01",
        picUserId: pickedUserId,
        picUserNm: pickedUserNm,
        jbpsSeNm: pickedJbpsSeNm,
        useYn: "Y",
      });

      closeMemberPick();
      saveAll(nextRows, "추가되었습니다.");
      return;
    }

    // edit: target row만 교체
    const nextRows = (dataArr || []).map((r: any) =>
      String(r.atrzStngId) === String(pickTargetAtrzStngId)
        ? { ...r, picUserId: pickedUserId, picUserNm: pickedUserNm, jbpsSeNm: pickedJbpsSeNm }
        : r
    );

    closeMemberPick();
    saveAll(nextRows, "수정되었습니다.");
  };

  // 삭제: NEW_면 프론트에서 제거 후 저장 / 기존이면 delete 후 저장
  const onDeleteRow = (row: any, idx: number) => {
    const stepText = `${idx + 1}차 결재자`;
    const nmText =
      row.picUserNm && row.picUserId
        ? `${row.picUserNm}(${row.picUserId})`
        : row.picUserId
        ? `(${row.picUserId})`
        : "";

    AppContext.openAlert({
      title: "확인",
      msg: `${stepText} ${nmText ? `- ${nmText} ` : ""}행을 삭제하시겠습니까?`,
      icon: "question",
      closeText: "취소",
      confirmText: "삭제",
      handleConfirm: () => {
        const atrzStngId = String(row.atrzStngId || "");
        const isNew = atrzStngId.startsWith("NEW_");

        if (isNew) {
          const nextRows = (dataArr || []).filter((r: any) => String(r.atrzStngId) !== atrzStngId);
          saveAll(nextRows, "삭제되었습니다.");
          return;
        }

        AppContext.showSpinner();
        Axios.dataAccess({
          url: "system/sys/aprvr/delete-aprvr.do",
          methodType: "post",
          paramType: "json",
          paramData: { atrzStngId },
          onSuccessFn: () => {
            AppContext.hideSpinner();
            const nextRows = (dataArr || []).filter((r: any) => String(r.atrzStngId) !== atrzStngId);
            saveAll(nextRows, "삭제되었습니다.");
          },
        });
      },
    });
  };

  // 모달 중복 선택 방지용
  const selectedUserIds = React.useMemo(() => {
    return (dataArr || [])
      .map((r: any) => String(r.picUserId || "").trim())
      .filter((v: string) => v !== "");
  }, [dataArr]);

  // edit 모드일 때만: 현재 담당자는 중복 예외
  const currentUserId = React.useMemo(() => {
    if (pickMode !== "edit") return "";
    const target = (dataArr || []).find((r: any) => String(r.atrzStngId) === String(pickTargetAtrzStngId));
    return String(target?.picUserId || "").trim();
  }, [dataArr, pickMode, pickTargetAtrzStngId]);

  return (
    <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
      <div className="mt5">
        <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">조합비 결재자 관리</span>
        <div className="bdt3 bdc007bff pb10"></div>
      </div>

      <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
        <div className="dpFlx aiC gap14 jcSb">
          <div className="dpFlx aiC gap14">
            <div className="dpFlx aiC">
              <span className="w40 fm-c fs15 fc333333 ls1 taL">본부</span>
              <SelectBox
                value={upOgnzCd}
                width={150}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                background={userInfo.systemAdmin === "Y" ? "#FFFFFF" : "#F6F6F6"}
                codeArray={Util.getUpOrgCodeArray("01")}
                placeholder="전체"
                paddingRight={8}
                disabled={userInfo.systemAdmin === "Y" ? false : true}
                onChangeFunc={(data: string) => {
                    setUpOgnzCd(data);
                    setOgnzCd("");
                }}
              />
            </div>

            <div className="dpFlx aiC">
              <span className="w50 fm-c fs15 fc333333 ls1 taL">지부</span>
              <SelectBox
                value={ognzCd}
                width={150}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                background={userInfo.systemAdmin === "Y" ? "#FFFFFF" : "#F6F6F6"}
                codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
                placeholder="전체"
                paddingRight={8}
                disabled={userInfo.systemAdmin === "Y" ? false : true}
                onChangeFunc={(data: string) => setOgnzCd(data)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 목록 타이틀 */}
      <div className="dpFlx aiC jcSb mb10 h44">
        <div className="fm-b fs16 fc007bff ls1 fw700">
          <span>결재자 목록</span>
        </div>
        <Button
          width={100}
          height={40}
          fontFamily="fm-b"
          fontSize={16}
          letterSpacing={-1}
          variant="styCompleteBtn"
          borderRadius={6}
          buttonClass="dpFlx aiC jcC"
          buttonName="추가"
          onClickFunc={onAdd}
        />
      </div>

      <div className="w100p bsBdb bgcFDFCFC bdt1 bdcC5C5C5">
        <table className="w100p tlF tbl-collapse">
          <colgroup>
            <col style={{ width: "15%" }} />
            <col style={{ width: "25%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "25%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>

          <thead>
            <tr>
              {["결재 단계", "결재 역할 (직책)", "소속기관", "결재자 이름(ID)", "수정", "삭제"].map((label) => (
                <th key={label} className="p0 bd1 bdcEDEDED">
                  <div className="h40 dpFlx aiC jcC pw12 bgcF1F8FF fm-Pretendard-Medium fw400 fs14 fc34495e ls1 wsNw ofH toE">
                    {label}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {dataArr.length === 0 ? (
              <tr>
                <td colSpan={4} className="p0 bd1 bdcEDEDED">
                  <div className="ph25 taC fc999999 fm-Pretendard-Regular fs13 ls075">조회된 데이터가 없습니다.</div>
                </td>
              </tr>
            ) : (
              dataArr.map((row: any, idx: number) => {
                const stageText = `${idx + 1}차 결재자`;
                const assigneeText = row.picUserNm
                  ? `${row.picUserNm} (${row.picUserId})`
                  : row.picUserId
                  ? `(${row.picUserId})`
                  : "";

                return (
                  <tr key={row.atrzStngId || idx} className="bgcF9F9F9-hover transitionBgc">
                    <td className="p0 bd1 bdcEDEDED">
                      <div className="h35 dpFlx aiC pw12 fm-Pretendard-Light fs13 fc333333 ls075 wsNw ofH toE">
                        {stageText}
                      </div>
                    </td>

                    <td className="p0 bd1 bdcEDEDED">
                      <div className="h35 dpFlx aiC pw12">
                        <div className="flx1 h30 dpFlx aiC fm-c fs13 fc333333 ls-1 wsNw ofH toE pl10">
                          {row.jbpsSeNm}
                        </div>
                      </div>
                    </td>

                    <td className="p0 bd1 bdcEDEDED">
                      <div className="h35 dpFlx aiC pw12">
                        <div className="flx1 h30 dpFlx aiC fm-c fs13 fc333333 ls-1 wsNw ofH toE pl10">
                          {row.ognzNm}
                        </div>
                      </div>
                    </td>

                    <td className="p0 bd1 bdcEDEDED">
                        <div className="h35 dpFlx aiC gap8 pw12">
                            <div className="flx1"
                                style={{fontSize: 13, fontFamily: "fm-c", color: "#333", letterSpacing: -1, }}
                            >
                                {assigneeText}
                            </div>
                        </div>
                    </td>

                    <td className="p0 bd1 bdcEDEDED">
                        <div className="h35 dpFlx aiC jcC">
                            <Button
                            width={70}
                            height={30}
                            fontFamily="fm-b"
                            fontSize={13}
                            letterSpacing={-1}
                            variant="styTempBtn"
                            borderRadius={6}
                            buttonClass="dpFlx aiC jcC"
                            buttonName="수정"
                            onClickFunc={() => openMemberPickForEdit(row.atrzStngId)}
                            />
                        </div>
                    </td>

                    <td className="p0 bd1 bdcEDEDED">
                      <div className="h35 dpFlx aiC jcC">
                        <Button
                          width={70}
                          height={30}
                          fontFamily="fm-b"
                          fontSize={13}
                          letterSpacing={-1}
                          variant="styDeleteBtn"
                          borderRadius={6}
                          buttonClass="dpFlx aiC jcC"
                          buttonName="삭제"
                          onClickFunc={() => onDeleteRow(row, idx)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Modal
        modalOpen={memberPickOpen}
        dim={true}
        visible={true}
        titleEl={<DialogTitleBar title={"담당자(회원) 선택"} />}
        onCloseFunc={closeMemberPick}
        width={800}
      >
        <div className="h900">
          <MbrPickDialog
            onPick={onPickMember}
            atrzSeCd={"01"}
            setModalOpen={setMemberPickOpen}
            initUpOgnzCd={upOgnzCd}
            initOgnzCd={ognzCd}
            selectedUserIds={selectedUserIds}
            currentUserId={pickMode === "edit" ? currentUserId : ""}
          />
        </div>
      </Modal>
    </div>
  );
};

export default SysAprvrMng2;
